import Konva from 'konva';
import { useRefs } from 'contexts/RefContext';
import { getRoofSegmentIDFromGroupID, KonvaGroupNames } from 'components/tool/utils';
import { useCallback, useEffect, useState } from 'react';
import { removeNewlyCreatedFacets, setDrawnFacetExists } from 'store/slices/ToolSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import CustomUndoBtn from 'components/tool/Editor/ButtonDiv/UndoButton';
import { selectActiveAction } from 'store/selectors/toolSelector';
import { getToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { GUIDE_GROUP_NAME, PREVIEW_DOT_NAME } from 'components/tool/Raster/DrawGuideLines';

export default function UndoButton() {

	const { konvaRef } = useRefs();
	const [undoEnabled, setUndoEnabled] = useState<boolean>(false);
	const { isCancelModalOpen } = useSelector((state: RootState) => state.toolNewPostions.data);
	const dispatch = useDispatch();
	const currentActiveActionOnTool = useSelector(selectActiveAction);
	const [confirmationOnUndo, setConfirmationOnUndo] = useState(false);

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage) return;
		const panelDrawerGroup = stage.findOne(`.${KonvaGroupNames.facetDrawingGroup}`) as Konva.Group;
		if (!panelDrawerGroup) return;
	
		const handleAdd = () => {
			const isDrawing = !!panelDrawerGroup.find('Group')?.length;
			setUndoEnabled(isDrawing);
		};
		panelDrawerGroup.on('add', handleAdd);
	
		return () => {
			panelDrawerGroup.off('add', handleAdd);
		};
	}, [konvaRef]);

	const undoFacetLines = useCallback(() => {
		if(isCancelModalOpen) return;
		if (!konvaRef?.current) return;

		const panelDrawerGroup = konvaRef.current.findOne(`.${KonvaGroupNames.facetDrawingGroup}`) as Konva.Group;
		if (!panelDrawerGroup?.hasChildren()) return;

		const allInProcessGroups = panelDrawerGroup.find(`.${KonvaGroupNames.inProgressUserDefinedFacetGroup}`) as Konva.Group[];
		if (!allInProcessGroups.length) return;

		const recentGroup = allInProcessGroups[allInProcessGroups.length -1] as Konva.Group;
		if (!recentGroup.children?.length) return;

		const children = recentGroup.children;
		const vertices = recentGroup.find('Circle') as Konva.Circle[];
		const facetLineLengthTextNodes: Konva.Text[] = [];
		let previewLineLengthTextNode: Konva.Text | null = null;

		(recentGroup.find('Text') as Konva.Text[]).forEach((textNode: Konva.Text) => {
			if (textNode.hasName('preview-line-text')) {
				previewLineLengthTextNode= textNode;
			} else {
				facetLineLengthTextNodes.push(textNode);
			}
		});

		const previewLine = recentGroup.findOne('.preview-line') as Konva.Line;
		previewLine?.points([]);
		if(previewLineLengthTextNode) (previewLineLengthTextNode as Konva.Text).visible(false);
		
		const facetLine = children[0] as Konva.Line;

		// SPECIAL CASE: In create facet mode, we dont have redo function so before removing 
		// the last vertex/facet we need to confirm the user really wants to remove and exit the mode.
		if(allInProcessGroups?.length == 1 && (vertices.length === 1 || facetLine?.closed())) {
			(panelDrawerGroup.findOne(`.${GUIDE_GROUP_NAME}`) as Konva.Group | undefined)?.destroyChildren();
			panelDrawerGroup.findOne(`.${PREVIEW_DOT_NAME}`)?.destroy();
			konvaRef.current.batchDraw();
			setConfirmationOnUndo(true);
			return;
		}

		if (facetLine) {
			if (facetLine?.closed()) {
				dispatch(removeNewlyCreatedFacets({ id: getRoofSegmentIDFromGroupID(facetLine.id()) }));
				recentGroup.destroy();
			}

			const points = facetLine.points();
			if (points.length >= 2) {
				points.splice(-2);
				facetLineLengthTextNodes.pop()?.destroy();
				vertices.pop()?.destroy();
				facetLine.points(points);
				facetLine.closed(false);
			} 

			if(!points.length) {
				recentGroup.destroy();
			}
		}

		(panelDrawerGroup.findOne(`.${GUIDE_GROUP_NAME}`) as Konva.Group | undefined)?.destroyChildren();
		panelDrawerGroup.findOne(`.${PREVIEW_DOT_NAME}`)?.destroy();
		konvaRef.current.batchDraw();
		konvaRef.current.fire('mousemove');

		const hasChildren = (panelDrawerGroup?.children?.length || 0) > 0;
		setUndoEnabled(hasChildren);
		dispatch(setDrawnFacetExists({ exists: hasChildren }));
	},[dispatch, isCancelModalOpen, konvaRef]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (((event.ctrlKey || event.metaKey) && event.key === 'z') || event.key === 'Escape') {
				undoFacetLines();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [undoFacetLines]);
	if (!undoEnabled) return null;

	function handleConfirmation() {
		if(currentActiveActionOnTool){
			const handler = getToolModeHandlers(currentActiveActionOnTool);
			handler?.cancel?.();
		}
		setConfirmationOnUndo(false);
	}

	return (
		<>
			<CustomUndoBtn
				id="undo-draw-Facet"
				onClick={undoFacetLines}
			/>
			<ConfirmationModal
				open={confirmationOnUndo}
				message={'Are you sure you want to discard the newly drawn facets?'}
				onConfirm={handleConfirmation}
				onCancel={() => {setConfirmationOnUndo(false);}}
			/>
		</>
	);

}